import React, { useState, useEffect, useMemo } from 'react';
import { Funnel, X, CaretDown } from '@phosphor-icons/react';
import type { QueryFilter } from '../../types/analytics';
import { analyticsFilterService } from '../../services/analyticsService';

// Type for filter config from widget template
interface WidgetFilterConfig {
  filter_key: string;
  filter_type: 'static' | 'dynamic';
  selection_type: 'single' | 'multiselect';
  required?: boolean;
  default_value?: string | string[];
}

// Type for filter options response from API
interface FilterOptionsResponse {
  filter_key: string;
  filter_name: string;
  filter_type: 'static' | 'dynamic';
  description?: string;
  options: Array<{ value: string | number; label: string }>;
}

// Helper to get filter key from config (handles both snake_case and camelCase)
const getFilterKey = (config: any): string => config.filterKey || config.filter_key || '';
const getFilterLabel = (config: any): string => config.filter_label || config.filterLabel || '';
const getFilterType = (config: any): string => config.filterType || config.filter_type || 'static';
const getSelectionType = (config: any): string => config.selectionType || config.selection_type || 'single';
const getRequired = (config: any): boolean => config.required ?? false;

interface WidgetFilterSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  availableFilters: WidgetFilterConfig[];
  currentFilters: QueryFilter[];
  onSaveFilters: (filters: QueryFilter[]) => void;
  widgetTitle: string;
}

export const WidgetFilterSelector: React.FC<WidgetFilterSelectorProps> = ({
  isOpen,
  onClose,
  availableFilters,
  currentFilters,
  onSaveFilters,
  widgetTitle,
}) => {
  const [filters, setFilters] = useState<QueryFilter[]>(currentFilters);
  const [filterOptions, setFilterOptions] = useState<Record<string, FilterOptionsResponse>>({});
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(currentFilters.map(f => f.field))
  );
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  // Filter out mandatory/required filters - they are shown in PersistentControls
  const displayableFilters = useMemo(() => {
    return availableFilters.filter((f) => !getRequired(f));
  }, [availableFilters]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpenPopover(null);
      }
    };

    if (openPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopover]);

  const togglePopover = (filterKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openPopover === filterKey) {
      setOpenPopover(null);
    } else {
      setOpenPopover(filterKey);
    }
  };

  // Fetch filter options from the filter registry using the service
  useEffect(() => {
    const fetchOptions = async () => {
      console.log('🔍 WidgetFilterSelector - Available Filters:', availableFilters);

      if (!availableFilters || availableFilters.length === 0) {
        console.warn('No available filters provided');
        setLoadError('No filters available for this widget');
        return;
      }

      setLoadingOptions(true);
      setLoadError(null);

      try {
        // Extract filter keys (handle both camelCase and snake_case)
        const filterKeys = availableFilters.map((f) => getFilterKey(f)).filter(Boolean);
        console.log('🔍 Fetching options for filter keys:', filterKeys);

        // Use the analytics filter service with caching
        const filterMap = await analyticsFilterService.batchGetFilterOptions(filterKeys, false);

        // Convert Map to Record for state
        const options: Record<string, FilterOptionsResponse> = {};
        filterMap.forEach((value, key) => {
          options[key] = value;
        });

        console.log('🔍 Final filter options (from service):', options);

        // Check if we got any options
        if (Object.keys(options).length === 0) {
          setLoadError('No filter options returned from server');
        }

        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load filter options';
        setLoadError(errorMessage);

        // Set empty fallback for all filters so UI can still render
        const fallbackOptions: Record<string, FilterOptionsResponse> = {};
        availableFilters.forEach((filterConfig) => {
          const key = getFilterKey(filterConfig);
          fallbackOptions[key] = {
            filter_key: key,
            filter_name: key,
            filter_type: getFilterType(filterConfig) as 'static' | 'dynamic',
            description: '',
            options: [],
          };
        });
        setFilterOptions(fallbackOptions);
      } finally {
        setLoadingOptions(false);
      }
    };

    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen, availableFilters]);

  const handleAddFilter = (filterKey: string) => {
    const filterConfig = displayableFilters.find((f) => getFilterKey(f) === filterKey);
    if (!filterConfig) return;

    const selectionType = getSelectionType(filterConfig);
    const newFilter: QueryFilter = {
      field: filterKey,
      operator: selectionType === 'multiselect' ? 'in' : '=',
      value: selectionType === 'multiselect' ? [] : '',
    };

    setFilters([...filters, newFilter]);
    setActiveFilters(new Set([...activeFilters, filterKey]));
    // Automatically open the popover for the new filter
    setOpenPopover(filterKey);
  };

  const handleUpdateFilter = (filterKey: string, value: string | string[]) => {
    const updatedFilters = filters.map((f) => {
      if (f.field === filterKey) {
        return {
          ...f,
          operator: (Array.isArray(value) ? 'in' : '=') as any,
          value,
        };
      }
      return f;
    });
    setFilters(updatedFilters);
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = filters.filter((f) => f.field !== filterKey);
    setFilters(newFilters);

    const newActiveFilters = new Set(activeFilters);
    newActiveFilters.delete(filterKey);
    setActiveFilters(newActiveFilters);
    if (openPopover === filterKey) {
      setOpenPopover(null);
    }
  };

  const handleSave = () => {
    // Only save filters that have values selected
    const validFilters: QueryFilter[] = filters.filter((filter) => {
      if (Array.isArray(filter.value)) {
        return filter.value.length > 0;
      }
      return filter.value !== null && filter.value !== '';
    });

    onSaveFilters(validFilters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'visible', // Allow popovers to overflow
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Funnel size={20} weight="fill" style={{ color: '#3b82f6' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
              Configure Filters - {widgetTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            <X size={20} weight="bold" style={{ color: '#6b7280' }} />
          </button>
        </div>

        {/* Error Message */}
        {loadError && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '13px',
            }}
          >
            <strong>Error:</strong> {loadError}
          </div>
        )}

        {/* Available Filters */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
            Available Filters
          </h3>
          {loadingOptions ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
              Loading filter options...
            </div>
          ) : displayableFilters.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
              No optional filters available for this widget
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {displayableFilters.map((filterConfig) => {
                const key = getFilterKey(filterConfig);
                const filterData = filterOptions[key];
                const isActive = activeFilters.has(key);

                return (
                  <button
                    key={key}
                    onClick={() => handleAddFilter(key)}
                    disabled={isActive}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: isActive ? '#f3f4f6' : 'white',
                      color: isActive ? '#9ca3af' : '#3b82f6',
                      border: '1px solid',
                      borderColor: isActive ? '#e5e7eb' : '#bfdbfe',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: isActive ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    + {getFilterLabel(filterConfig) || filterData?.filter_name || key}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.size > 0 && (
          <div style={{ marginBottom: '24px', flex: 1, overflow: 'visible' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              Active Filters
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {Array.from(activeFilters).map((filterKey) => {
                const filterConfig = displayableFilters.find((f) => getFilterKey(f) === filterKey);
                const filterData = filterOptions[filterKey];
                const filterValue = filters.find((f) => f.field === filterKey);

                // Allow rendering even if filterData is missing - use fallback values
                if (!filterConfig || !filterValue) return null;
                const selectionType = getSelectionType(filterConfig);
                const required = getRequired(filterConfig);

                const displayName = getFilterLabel(filterConfig) || filterData?.filter_name || filterKey;
                const description = filterData?.description || '';
                const options = filterData?.options || [];

                const isPopoverOpen = openPopover === filterKey;

                // Calculate summary text
                let summaryText = '';
                if (filterValue && filterValue.value !== null && filterValue.value !== undefined) {
                  if (Array.isArray(filterValue.value)) {
                    const count = filterValue.value.length;
                    if (count === 0) summaryText = 'Select options';
                    else if (count === 1) {
                      const val = filterValue.value[0];
                      const opt = options.find(o => String(o.value) === String(val));
                      summaryText = opt ? opt.label : String(val);
                    } else if (count <= 2) {
                      const labels = filterValue.value.map(val => {
                        const opt = options.find(o => String(o.value) === String(val));
                        return opt ? opt.label : String(val);
                      });
                      summaryText = labels.join(', ');
                    } else {
                      summaryText = `${count} selected`;
                    }
                  } else {
                    const opt = options.find(o => String(o.value) === String(filterValue.value));
                    summaryText = opt ? opt.label : String(filterValue.value);
                  }
                } else {
                  summaryText = 'Select option';
                }

                return (
                  <div
                    key={filterKey}
                    style={{ position: 'relative' }}
                  >
                    {/* Two-Part Chip */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        backgroundColor: 'white',
                        border: `1px solid ${isPopoverOpen ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      {/* Left Part: Filter Name */}
                      <div
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f9fafb',
                          borderRight: '1px solid #e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        {displayName}
                      </div>

                      {/* Right Part: Value Summary */}
                      <div
                        onClick={(e) => togglePopover(filterKey, e)}
                        style={{
                          padding: '6px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '13px',
                          color: '#111827',
                          minWidth: '100px',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>
                          {summaryText}
                        </span>
                        <CaretDown size={12} weight="bold" style={{ color: '#6b7280' }} />
                      </div>

                      {/* Remove Button */}
                      {!required && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter(filterKey);
                          }}
                          style={{
                            padding: '0 8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderLeft: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#9ca3af',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fee2e2';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#9ca3af';
                          }}
                          title="Remove filter"
                        >
                          <X size={14} weight="bold" />
                        </button>
                      )}
                    </div>

                    {/* Popover */}
                    {isPopoverOpen && (
                      <div
                        ref={popoverRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '8px',
                          width: '300px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          border: '1px solid #e5e7eb',
                          zIndex: 50,
                          padding: '16px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {description && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                            {description}
                          </div>
                        )}

                        {options.length === 0 ? (
                          <div style={{ padding: '8px', color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
                            No options available.
                          </div>
                        ) : selectionType === 'multiselect' ? (
                          <>
                            <div
                              style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                padding: '8px',
                              }}
                            >
                              {options.map((option) => {
                                const currentValues = Array.isArray(filterValue?.value) ? filterValue.value.map(String) : [];
                                const isSelected = currentValues.includes(String(option.value));

                                return (
                                  <label
                                    key={option.value}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '8px',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                      transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        const newValue = String(option.value);
                                        let newValues: string[];
                                        if (e.target.checked) {
                                          newValues = [...currentValues, newValue];
                                        } else {
                                          newValues = currentValues.filter(v => v !== newValue);
                                        }
                                        handleUpdateFilter(filterKey, newValues);
                                      }}
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        accentColor: '#3b82f6',
                                        cursor: 'pointer',
                                      }}
                                    />
                                    <span style={{ fontSize: '13px', color: '#374151' }}>
                                      {option.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                            <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                              Select one or more options
                            </p>
                          </>
                        ) : (
                          <div
                            style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              padding: '8px',
                            }}
                          >
                            {options.map((option) => {
                              const isSelected = String(filterValue?.value) === String(option.value);

                              return (
                                <label
                                  key={option.value}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`filter-${filterKey}`}
                                    value={option.value}
                                    checked={isSelected}
                                    onChange={(e) => handleUpdateFilter(filterKey, e.target.value)}
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      accentColor: '#3b82f6',
                                      cursor: 'pointer',
                                    }}
                                  />
                                  <span style={{ fontSize: '13px', color: '#374151' }}>
                                    {option.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default WidgetFilterSelector;
